/* =====================================================================
   AstaLab 2.0 — importPreview.js (round alpha.2, obiettivo 5)

   Porting adattato di buildListoneUpdatePreview()/buildStatisticsImportPreview()
   dalla baseline 1.3.0-beta.6 (stessa pipeline: parse → normalize →
   validate → preview, MAI una scrittura prima della conferma esplicita
   — vedi CatalogRepository, che è l'unico punto a valle che scrive
   davvero). Nessuna dipendenza da STATE/candidate: prendono solo bytes
   e restituiscono un oggetto preview di sola lettura.
   ===================================================================== */
import { classifyImportFile, parseXLSXWorkbook, pickPrimarySheet } from "./xlsxParser.js";
import { toStr } from "./normalizeUtils.js";
import {
  parseCSV, detectDelimiter, parseQuotazioniRowsStrict, parseStatisticheRowsStrict,
  detectSeasonFromText, computeFileChecksum
} from "./statisticsEngine.js";

function decodeRows(bytes, fileName, mimeType){
  const kind = classifyImportFile(fileName, mimeType, bytes);
  let rows, rawTitle = "";
  if(kind === "xlsx"){
    const wb = parseXLSXWorkbook(bytes);
    const primary = pickPrimarySheet(wb);
    rows = primary.rows;
    rawTitle = (rows[0]||[]).map(c=>toStr(c,"")).join(" ");
  } else {
    const text = new TextDecoder("utf-8").decode(bytes);
    rows = parseCSV(text, detectDelimiter(text));
    rawTitle = (rows[0]||[]).join(" ");
  }
  return { kind, rows, rawTitle };
}

/** Revisione r5 (audit, blocker 1/2/3/4): percorso di import 2.0 — usa la pipeline STRICT
 *  (parseQuotazioniRowsStrict), MAI il parser legacy tollerante (che scarta silenziosamente le
 *  righe invalide pur restituendo ok:true — pericoloso per un import che rappresenta uno SNAPSHOT
 *  COMPLETO del catalogo: una riga malformata per un fantacalcio_id realmente esistente
 *  diventerebbe silenziosamente un giocatore "uscito"). Se anche una sola riga dati è invalida,
 *  o un header ufficiale richiesto manca, l'INTERO file viene rifiutato qui, PRIMA che il
 *  chiamante veda un preview.ok=true — mai un round/clamp/default silenzioso. La stagione deve
 *  ora essere ricavabile con certezza dal nome file/titolo del foglio (blocker 4): un listone con
 *  season_key indeterminata non può più essere confermato (import_catalog_snapshot non viene mai
 *  chiamata con p_season_key=null da questo percorso).
 *  @returns {{ok:true, kind, records, errori, sourceFile, rowCount, season, fileChecksum}|{ok:false, error}} */
export function buildListoneUpdatePreview(bytes, fileName, mimeType){
  let decoded;
  try{ decoded = decodeRows(bytes, fileName, mimeType); }
  catch(e){ return {ok:false, error:"Impossibile analizzare il file: "+e.message}; }
  const parsed = parseQuotazioniRowsStrict(decoded.rows);
  if(!parsed.ok) return {ok:false, error:parsed.error};
  const season = detectSeasonFromText(fileName) || detectSeasonFromText(decoded.rawTitle) || null;
  if(!season) return {ok:false, error:"Impossibile riconoscere la stagione dal nome del file o dal titolo del foglio — rinomina il file includendo l'anno (es. \"Quotazioni_2026_27\") e riprova. Un listone (snapshot completo) non può essere importato senza una stagione certa."};
  // fileChecksum sempre calcolato (obiettivo 5: "l'import deve essere idempotente tramite
  // checksum/file identity") — usato da SupabaseCatalogRepository per far riconoscere a
  // import_catalog_snapshot() un reimport dello stesso identico file (vedi 0008_catalog_rpc_import.sql).
  const fileChecksum = computeFileChecksum(bytes);
  return {ok:true, kind:decoded.kind, records:parsed.records, errori:parsed.errori, sourceFile:fileName, rowCount:parsed.records.length, season, fileChecksum};
}

/** Revisione r5 (audit, blocker 5): stessa pipeline STRICT delle statistiche ufficiali (percorso
 *  2.0) — mai un round/clamp/default silenzioso su PV/MV/FM/GF/GS/RP/RC/rigori/assist/
 *  ammonizioni/espulsioni/autogol; `0` resta sempre un valore reale, un campo vuoto resta NULL.
 *  @returns {{ok:true, kind, season, records, errori, sourceFile, rowCount, fileChecksum, columnsFound}|{ok:false, error}} */
export function buildStatisticsImportPreview(bytes, fileName, mimeType){
  let decoded;
  try{ decoded = decodeRows(bytes, fileName, mimeType); }
  catch(e){ return {ok:false, error:"Impossibile analizzare il file: "+e.message}; }
  const parsed = parseStatisticheRowsStrict(decoded.rows);
  if(!parsed.ok) return {ok:false, error:parsed.error};
  const season = detectSeasonFromText(fileName) || detectSeasonFromText(decoded.rawTitle);
  if(!season) return {ok:false, error:"Impossibile riconoscere la stagione dal nome del file o dal titolo del foglio — rinomina il file includendo l'anno (es. \"Statistiche_2025_26\") e riprova."};
  const fileChecksum = computeFileChecksum(bytes);
  return {ok:true, kind:decoded.kind, season, records:parsed.records, errori:parsed.errori, sourceFile:fileName, rowCount:parsed.records.length, fileChecksum, columnsFound:parsed.columnsFound};
}
